import { splitNewExamplesFromExistingEditedExamples } from '../utils';
import { useCreateDatasetWithExamples } from './useCreateDatasetWithExamples';
import {
  isDatasetExamplesEdited,
  usePlaygroundStore,
} from './usePlaygroundStore';
import { useUpdateAndCreateDatasetExamples } from './useUpdateAndCreateDatasetExamples';

/**
 * Hook that provides functionality to save a dataset, handling both creation of new datasets
 * and updating existing ones.
 *
 * @returns Object containing save function, loading state, and whether the action is disabled
 */
export const useSaveDataset = ({
  newDatasetName,
  onSuccess,
}: {
  newDatasetName?: string;
  onSuccess?: () => void;
}) => {
  const datasetId = usePlaygroundStore((state) => state.datasetId);
  const isCreatingNewDataset = usePlaygroundStore(
    (state) => state.isCreatingNewDataset
  );
  const examples = usePlaygroundStore((state) => state.examples);
  const { newExamples, editedExistingExamples } =
    splitNewExamplesFromExistingEditedExamples(examples);
  const setDatasetId = usePlaygroundStore(
    (state) => state.setDatasetIdAndUpdateUrl
  );
  const markExamplesAsSaved = usePlaygroundStore(
    (state) => state.markExamplesAsSaved
  );
  const isDatasetEdited = isDatasetExamplesEdited(examples);

  // Hook for creating new datasets
  const { isLoading: isCreating, saveDataset: createDataset } =
    useCreateDatasetWithExamples({
      examples,
      onSuccess: (dataset) => {
        setDatasetId(dataset.id);
        markExamplesAsSaved();
        onSuccess?.();
      },
    });

  // Hook for updating existing datasets
  const { isLoading: isUpdating, updateDataset } =
    useUpdateAndCreateDatasetExamples({
      datasetId: datasetId ?? '',
      onSuccess: () => {
        markExamplesAsSaved();
        onSuccess?.();
      },
    });

  const isLoading = isCreating || isUpdating;
  const disabledMessage = isLoading
    ? 'Saving...'
    : !isCreatingNewDataset && (!datasetId || !isDatasetEdited)
    ? 'No changes to save'
    : undefined;

  // Function that handles both creating and updating
  async function saveDataset() {
    if (isCreatingNewDataset) {
      await createDataset(newDatasetName);
    } else if (datasetId) {
      await updateDataset(newExamples, editedExistingExamples);
    }
  }

  return {
    saveDataset,
    isLoading,
    disabledMessage,
    isCreatingNewDataset,
  };
};
